import { Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet
} from '@angular/router';
import { fromEvent, map, merge, of, startWith, switchMap, timer } from 'rxjs';
import { BroadcastChannelMessageTypes } from './core/enums/BroadcastChannelMessageTypes';
import { BaseComponent } from './core/layout/components/abstracts/base.component';
import { MainLayoutComponent } from './core/layout/layouts/main-layout/main-layout.component';
import { BackgroundActionsService } from './core/services/BackgroundActionsService';
import { BroadcastChannelService } from './core/services/BroadcastChannelService';
import { CustomerScreenService } from './core/services/CustomerScreenService';
import { LocalStorageService } from './core/services/LocalStorageService';
import { BrowserUtils } from './core/utils/BrowserUtils';
import { GuidUtils } from './core/utils/GuidUtils';
import { WorkerService } from './features/worker/core/services/WorkerService';
import { SeatNoteRefreshService } from './features/cart/core/services/SeatNoteRefreshService';
import { AlertModalComponent } from './shared/components/alert-modal/alert-modal.component';
import { ConfirmationModalComponent } from './shared/components/confirmation-modal/confirmation-modal.component';
import { TubeOverlayViewComponent } from './features/tube/views/tube-overlay-view/tube-overlay-view.component';
import { SupportModeBannerComponent } from './features/support-mode/views/support-mode-banner/support-mode-banner.component';
import { AppStore } from './core/stores/AppStore';
import { AutoScrollService } from './core/services/AutoScrollService';
import { ScannerService } from './core/services/ScannerService';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    ConfirmationModalComponent,
    AlertModalComponent,
    TubeOverlayViewComponent,
    SupportModeBannerComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent extends BaseComponent {

  public readonly isLoading = signal(false);

  private readonly _router = inject(Router);
  private readonly _broadcastChannelService = inject(BroadcastChannelService);
  private readonly _customerScreenService = inject(CustomerScreenService);
  private readonly _backgroundActionsService = inject(BackgroundActionsService);
  private readonly _localStorageService = inject(LocalStorageService);
  private readonly _workerService = inject(WorkerService);
  private readonly _autoScrollService = inject(AutoScrollService);
  private readonly _seatNoteRefreshService = inject(SeatNoteRefreshService);
  private readonly _scannerService = inject(ScannerService);

  public ngOnInit(): void {
    this._localStorageService.setItem('cash-key', GuidUtils.raw());
    this._broadcastChannelService.startCashChannel();
    this.initCustomerScreenHandling();
    this.initAutoScrollService();

    if (!this.isCustomerScreen) {
      this.initScannerListener();
      this._seatNoteRefreshService.initialize();
    }

    this._router.events.subscribe((event) => {
      switch (true) {
        case event instanceof NavigationStart: {
          this.isLoading.set(true);
          break;
        }

        case event instanceof NavigationEnd:
        case event instanceof NavigationCancel:
        case event instanceof NavigationError: {
          this.isLoading.set(false);
          break;
        }

        default: {
          break;
        }
      }
    });
  }

  private initCustomerScreenHandling(): void {
    if (!this.isCustomerScreen) {
      fromEvent<BeforeUnloadEvent>(window, 'beforeunload').subscribe(event => {
        if (!BrowserUtils.isLocalhost && AppStore.shouldPreventAppReload()) {
          event.preventDefault();
        }
      });

      this._customerScreenService.lastPingTime$
        .pipe(
          switchMap(() =>
            merge(
              of(true),
              timer(1000).pipe(map(() => false))
            )
          ),
          startWith(false)
        )
        .subscribe(val => {
          MainLayoutComponent.instance?.isCustomerScreenOnline.set(val);

          if (val) {
            this._broadcastChannelService.cashChannelPostMessage(
              {
                type: BroadcastChannelMessageTypes.PONG,
                data: {}
              }
            );
          }
        });
    } else {
      this._backgroundActionsService.pingCustomerScreen.start();

      this._customerScreenService.lastPongTime$
        .pipe(
          switchMap(() =>
            merge(
              of(true),
              timer(3000).pipe(map(() => false))
            )
          ),
          startWith(true),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(val => {
          if (!val) {
            this._workerService.closeCustomerScreen().subscribe();
          }
        });
    }
  }

  private initAutoScrollService(): void {
    this._autoScrollService.initialize();
  }

  private initScannerListener(): void {
    let scannedVal = '';
    let timer: ReturnType<typeof setTimeout> | null = null;

    fromEvent<KeyboardEvent>(document, 'keydown')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: KeyboardEvent) => {
        if (this._scannerService.suspended) {
          return;
        }

        const textInput = event.key || String.fromCharCode(event.keyCode);

        const target = event.target;
        const isFormField =
          target instanceof HTMLElement
          && !!target.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]');

        if (textInput.length === 1 && !isFormField) {
          scannedVal += textInput;

          if (timer) {
            clearTimeout(timer);
          }

          timer = setTimeout(() => {
            if (this._scannerService.suspended) {
              scannedVal = '';
              return;
            }

            this._scannerService.scannedVal$.next(scannedVal);
            scannedVal = '';
          }, 100);
        }
      });
  }
}
